<template>
  <main ref="industriesPage" class="industries-page">
    <template v-if="industry">
      <section class="industry-hero" :style="heroStyle" aria-labelledby="industry-title">
        <div class="industry-hero__overlay"></div>
        <div class="industry-hero__content">
          <h1 id="industry-title">{{ industry.title }}</h1>
          <p>{{ industry.description }}</p>
        </div>
      </section>

      <section ref="scenariosSection" class="industry-scenarios industry-scenarios-reveal" aria-labelledby="scenarios-title">
        <div class="industries-container">
          <h2 id="scenarios-title" class="industry-scenarios__heading">{{ t('industries.scenarios') }}</h2>
          <div class="scenario-grid industry-scenarios__cards">
            <article v-for="scenario in industry.scenarios" :key="scenario.id" class="scenario-card">
              <div class="scenario-card__image">
                <img :src="scenario.coverUrl" :alt="scenario.title" loading="lazy" decoding="async" @load="markMediaReady" @error="markMediaReady">
              </div>
              <div class="scenario-card__content">
                <h3>{{ scenario.title }}</h3>
                <p>{{ scenario.description }}</p>
                <router-link v-if="scenario.detailPath" class="scenario-card__learn-more" :to="scenario.detailPath">
                  {{ t('actions.industryLearnMore') }}
                </router-link>
                <button v-else class="scenario-card__learn-more" type="button" disabled aria-disabled="true">{{ t('actions.industryLearnMore') }}</button>
              </div>
            </article>
          </div>
        </div>
      </section>
    </template>

    <section v-else class="industry-not-found" aria-labelledby="industry-not-found-title">
      <h1 id="industry-not-found-title">Industry not found</h1>
      <router-link to="/">Return home</router-link>
    </section>
  </main>
</template>

<script>
import { solutionAPI } from '@/utils/api'

const {
  buildIndustryDetailPath,
  resolveIndustryLanguagePath,
  normalizeIndustryCategoriesResponse,
  normalizeIndustryTopicsResponse
} = require('@/utils/industries-data.cjs')
const { siteLanguageMixin } = require('@/utils/site-language.js')
const { createLatestSiteRequestGuard } = require('@/utils/site-request.cjs')

export default {
  name: 'Industries',
  mixins: [siteLanguageMixin],
  data() {
    return {
      industry: null,
      industryRequestGuard: createLatestSiteRequestGuard(),
      pageRevealObserver: null
    }
  },
  computed: {
    heroStyle() {
      return this.industry ? { backgroundImage: `url(${this.industry.heroImage})` } : {}
    }
  },
  watch: {
    '$route.params.categoryId'() {
      this.fetchIndustry()
    },
    currentLanguage() {
      this.redirectToCurrentLanguageIndustry()
    }
  },
  async mounted() {
    await this.fetchIndustry()
    this.$nextTick(this.initPageReveal)
  },
  beforeUnmount() {
    if (this.pageRevealObserver) this.pageRevealObserver.disconnect()
  },
  methods: {
    initPageReveal() {
      const page = this.$refs.industriesPage
      if (!page || window.matchMedia('(prefers-reduced-motion: reduce)').matches || typeof window.IntersectionObserver !== 'function') return
      const target = this.$refs.scenariosSection
      if (!target || this.pageRevealObserver) return

      try {
        this.pageRevealObserver = new window.IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting && entry.boundingClientRect.top >= 0) return
            entry.target.classList.add('is-revealed')
            this.pageRevealObserver.unobserve(entry.target)
          })
        }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' })
        this.pageRevealObserver.observe(target)
        page.classList.add('reveal-enabled')
      } catch (error) {
        if (this.pageRevealObserver) this.pageRevealObserver.disconnect()
        this.pageRevealObserver = null
      }
    },
    markMediaReady(event) {
      const media = event && (event.currentTarget || event.target)
      if (media && media.classList) media.classList.add('is-media-ready')
    },
    async refreshScenarioReveal() {
      await this.$nextTick()
      if (this.pageRevealObserver) {
        this.pageRevealObserver.disconnect()
        this.pageRevealObserver = null
      }
      this.initPageReveal()
    },
    async fetchIndustry() {
      const request = this.industryRequestGuard.start(this.currentLanguage)
      this.industry = null
      const categoryId = this.$route.params.categoryId
      if (!categoryId) return
      try {
        const categories = normalizeIndustryCategoriesResponse(await solutionAPI.getCategories(request.siteType))
        if (!this.industryRequestGuard.isCurrent(request, this.currentLanguage)) return
        const category = categories.find((item) => String(item.categoryId) === String(categoryId))
        if (!category) return
        const topics = normalizeIndustryTopicsResponse(await solutionAPI.getTopics(category.categoryId, request.siteType))
        if (!this.industryRequestGuard.isCurrent(request, this.currentLanguage)) return
        this.industry = {
          ...category,
          scenarios: topics.map((topic) => ({
            ...topic,
            coverUrl: topic.cardCoverUrl,
            detailPath: buildIndustryDetailPath(category.categoryId, topic.id)
          }))
        }
        await this.refreshScenarioReveal()
      } catch (error) {
        if (!this.industryRequestGuard.isCurrent(request, this.currentLanguage)) return
        console.error('Failed to load industries:', error)
      }
    },
    async redirectToCurrentLanguageIndustry() {
      const request = this.industryRequestGuard.start(this.currentLanguage)
      this.industry = null
      try {
        const categories = normalizeIndustryCategoriesResponse(await solutionAPI.getCategories(request.siteType))
        if (!this.industryRequestGuard.isCurrent(request, this.currentLanguage)) return
        const targetPath = resolveIndustryLanguagePath(categories)
        if (this.$route.path === targetPath) {
          await this.fetchIndustry()
          return
        }
        await this.$router.replace(targetPath)
      } catch (error) {
        if (!this.industryRequestGuard.isCurrent(request, this.currentLanguage)) return
        console.error('Failed to switch industries language:', error)
        await this.$router.replace('/')
      }
    }
  }
}
</script>

<style scoped>
.industries-page {
  min-height: 100vh;
  padding-top: var(--global-header-height, 44px);
  color: #000;
  background: #fff;
  font-family: MiSans, Arial, sans-serif;
}

.industries-container {
  width: min(1296px, calc(100vw - 144px));
  margin-left: max(72px, calc(50vw - 648px));
}

.industry-hero {
  width: min(1296px, calc(100vw - 144px));
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 397px;
  margin-top: 60px;
  margin-left: max(72px, calc(50vw - 648px));
  overflow: hidden;
  background-color: #222;
  background-position: center;
  background-size: cover;
}

.industry-hero__overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
}

.industry-hero__content {
  position: relative;
  z-index: 1;
  width: min(100% - 48px, 640px);
  color: #fff;
  text-align: center;
}

.industry-hero h1 {
  margin: 0;
  color: #fff;
  font-size: 58px;
  font-weight: 700;
  line-height: 1.22;
  text-transform: uppercase;
}

.industry-hero p {
  max-width: 520px;
  margin: 27px auto 0;
  font-size: 20px;
  line-height: 1.4;
}

.industry-scenarios {
  padding: 80px 0;
}

.industry-scenarios h2 {
  margin: 0;
  font-size: 40px;
  font-weight: 700;
  line-height: 1.3;
  text-align: center;
}

.scenario-grid {
  display: grid;
  grid-template-columns: repeat(3, 418px);
  gap: 40px 21px;
  margin-top: 48px;
}

.scenario-card {
  display: flex;
  min-width: 0;
  height: 412px;
  overflow: hidden;
  flex-direction: column;
  background: #f5f5f7;
}

.scenario-card__image {
  height: 235px;
  overflow: hidden;
  background: #e8e8ea;
}

.scenario-card__image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 240ms ease;
}

.scenario-card__image img.is-media-ready {
  opacity: 1;
}

.scenario-card__content {
  display: flex;
  height: 177px;
  min-height: 0;
  flex: 0 0 177px;
  flex-direction: column;
  padding: 20px;
  box-sizing: border-box;
}

.scenario-card h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.35;
}

.scenario-card p {
  display: -webkit-box;
  overflow: hidden;
  margin: 12px 0 0;
  color: #000;
  font-size: 14px;
  line-height: 1.45;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.scenario-card__learn-more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  height: 30px;
  margin-top: auto;
  padding: 3px 14px;
  border: 1px solid #000;
  border-radius: 67px;
  color: #000;
  background: transparent;
  font: inherit;
  font-size: 10px;
  line-height: 1;
  text-decoration: none;
  cursor: not-allowed;
}

@media (min-width: 701px) {
  .industries-page.reveal-enabled .industry-scenarios__heading,
  .industries-page.reveal-enabled .industry-scenarios-reveal .scenario-card {
    opacity: 1;
    transform: translateY(0);
    transition: opacity 620ms ease-out, transform 620ms ease-out;
  }

  .industries-page.reveal-enabled .industry-scenarios-reveal:not(.is-revealed) .industry-scenarios__heading,
  .industries-page.reveal-enabled .industry-scenarios-reveal:not(.is-revealed) .scenario-card {
    opacity: 0;
    transform: translateY(30px);
  }

  .industries-page.reveal-enabled .industry-scenarios-reveal .scenario-card:nth-child(3n + 2) {
    transition-delay: 80ms;
  }

  .industries-page.reveal-enabled .industry-scenarios-reveal .scenario-card:nth-child(3n) {
    transition-delay: 160ms;
  }
}

a.scenario-card__learn-more {
  cursor: pointer;
}

a.scenario-card__learn-more:hover,
a.scenario-card__learn-more:focus-visible {
  border-color: #ef0a6a;
  color: #fff;
  background: #ef0a6a;
  outline: none;
}

.industry-not-found {
  display: flex;
  min-height: calc(100vh - var(--global-header-height, 44px));
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 18px;
  padding: 32px 16px;
  text-align: center;
}

.industry-not-found h1 {
  margin: 0;
  font-size: 32px;
}

.industry-not-found a {
  color: #ef0a6a;
}

@media (max-width: 900px) {
  .industries-container,
  .industry-hero {
    width: calc(100vw - 48px);
    margin-left: 24px;
  }

  .scenario-grid {
    gap: 24px;
  }
}

@media (min-width: 701px) and (max-width: 1379px) {
  .scenario-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 700px) {
  .industries-page.reveal-enabled .industry-scenarios-reveal {
    opacity: 1;
    transform: translateY(0);
    transition: opacity 560ms ease-out, transform 560ms ease-out;
  }

  .industries-page.reveal-enabled .industry-scenarios-reveal:not(.is-revealed) {
    opacity: 0;
    transform: translateY(20px);
  }

  .industries-page {
    padding-top: var(--global-header-height, 50px);
  }

  .industries-container,
  .industry-hero {
    width: min(400px, calc(100vw - 32px));
    margin-left: 16px;
  }

  .industry-hero {
    height: 180px;
    margin-top: 0;
  }

  .industry-hero__content {
    width: calc(100% - 32px);
  }

  .industry-hero h1 {
    font-size: 30px;
    line-height: 1.2;
  }

  .industry-hero p {
    margin-top: 10px;
    font-size: 12px;
    line-height: 1.35;
  }

  .industry-scenarios {
    padding: 60px 0 74px;
  }

  .industry-scenarios h2 {
    font-size: 30px;
  }

  .scenario-grid {
    grid-template-columns: 1fr;
    gap: 30px;
    margin-top: 31px;
  }

  .scenario-card {
    height: 412px;
  }

  .scenario-card__image {
    height: 235px;
  }

  .scenario-card__content {
    height: 177px;
    min-height: 0;
    flex-basis: 177px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .industries-page.reveal-enabled .industry-scenarios-reveal,
  .industries-page.reveal-enabled .industry-scenarios__heading,
  .industries-page.reveal-enabled .industry-scenarios-reveal .scenario-card {
    opacity: 1;
    transform: none;
    transition: none;
  }

  .scenario-card__image img {
    opacity: 1;
    transition: none;
  }
}
</style>
